import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import assignmentService from "../../services/assignmentService";
import { queryKeys } from "./queryKeys";

// Fetch assignments
export const useAssignments = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.assignments.list(filters),
    queryFn: () => assignmentService.getAssignments(filters),
    staleTime: 5 * 60 * 1000,
  });
};

// Fetch department assignments of HOD
export const useDepartmentAssignmentsOfHod = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.assignments.departmentAssignmentsOfHod(params),
    queryFn: () => {
      console.log("useDepartmentAssignmentsOfHod - queryFn called", params);
      return assignmentService.getDepartmentAssignmentsOfHod(params);
    },
    staleTime: 0, // Always consider data stale, refetch on mount
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
    enabled: true, // Explicitly enable the query
  });
};

// Fetch single assignment
export const useAssignment = (id) => {
  return useQuery({
    queryKey: queryKeys.assignments.detail(id),
    queryFn: () => assignmentService.getAssignmentById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// Create assignment
export const useCreateAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => assignmentService.createAssignment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.departmentAssignmentsOfHod(),
      });
    },
  });
};

// Update assignment
export const useUpdateAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) =>
      assignmentService.updateAssignment(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.departmentAssignmentsOfHod(),
      });
    },
  });
};

// Delete assignment
export const useDeleteAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => assignmentService.deleteAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.departmentAssignmentsOfHod(),
      });
    },
  });
};

// Submit assignment
export const useSubmitAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assignmentId, submissionData }) =>
      assignmentService.submitAssignment(assignmentId, submissionData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.detail(variables.assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.submissions(variables.assignmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.lists(),
      });
    },
  });
};

// Fetch submissions for an assignment
export const useSubmissions = (assignmentId) => {
  return useQuery({
    queryKey: queryKeys.assignments.submissions(assignmentId),
    queryFn: () => assignmentService.getSubmissions(assignmentId),
    enabled: !!assignmentId,
    staleTime: 2 * 60 * 1000,
  });
};

// Fetch single submission
export const useSubmission = (assignmentId, submissionId) => {
  return useQuery({
    queryKey: queryKeys.assignments.submission(assignmentId, submissionId),
    queryFn: () =>
      assignmentService.getSubmissionById(assignmentId, submissionId),
    enabled: !!assignmentId && !!submissionId,
    staleTime: 2 * 60 * 1000,
  });
};

// Approve submission
export const useApproveSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submissionId, data }) =>
      assignmentService.approveSubmission(submissionId, data),
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.all,
      });
    },
  });
};

// Reject submission
export const useRejectSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submissionId, data }) =>
      assignmentService.rejectSubmission(submissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.assignments.all,
      });
    },
  });
};

// Materials queries and mutations
export const useMaterials = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.materials.list(filters),
    queryFn: () => assignmentService.getMaterials(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useMaterial = (id) => {
  return useQuery({
    queryKey: queryKeys.materials.detail(id),
    queryFn: () => assignmentService.getMaterialById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => assignmentService.createMaterial(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.materials.lists(),
      });
    },
  });
};

export const useUpdateMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => assignmentService.updateMaterial(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.materials.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.materials.lists(),
      });
    },
  });
};

export const useDeleteMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => assignmentService.deleteMaterial(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.materials.lists(),
      });
    },
  });
};

export const useDownloadMaterial = () => {
  return useMutation({
    mutationFn: (id) => assignmentService.downloadMaterial(id),
  });
};

